import {DownOutlined, ExclamationCircleOutlined} from '@ant-design/icons/lib';
import {Avatar, Button, Card, Dropdown, Menu, Modal, Typography} from 'antd';
import {CardProps} from 'antd/es/card';
import {
  useDisableUserMutation,
  useEnableUserMutation,
  useImpersonateMutation,
  useUpdateUserMutation,
  useUserQuery
} from 'api/generated';
import {PageHeader, Stack} from 'components';
import {EmailLink} from 'components/EmailLink';
import {PropertyList} from 'components/PropertyList';
import {UserStatusTag} from 'components/UserStatusTag';
import gravatar from 'gravatar';
import initials from 'initials';
import {colors} from 'lib/colors';
import {formatBoolean, formatPhone} from 'lib/formaters';
import {UserContext} from 'lib/userContext';
import moment from 'moment';
import React, {useContext, useState} from 'react';
import {Link, RouteComponentProps} from 'react-router-dom';
import {ResetPassword} from 'routes/User/ResetPassword';
import {config} from 'stage.config';
import {NotFound} from '../NotFound';

const {Text} = Typography;

export const User: React.FC<RouteComponentProps<{userId: string}>> = ({match}) => {
  const {userId} = match.params;
  const {data, loading} = useUserQuery({
    variables: {id: userId},
    context: {useApolloNetworkStatus: false}
  });
  const [impersonate, {loading: isImpersonating}] = useImpersonateMutation();
  const [disableUser, {loading: isDisabling}] = useDisableUserMutation();
  const [enableUser, {loading: isEnabling}] = useEnableUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const {userEmail} = useContext(UserContext);
  const user = data?.user;
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);

  if (!user && !loading) {
    return (
      <NotFound
        message={
          <>
            Sorry, user with id <strong>{userId}</strong> does not exist
          </>
        }
      />
    );
  }

  const impersonateUser = async () => {
    if (!user?.email) return;
    await impersonate({
      variables: {
        input: {
          adminEmail: userEmail,
          userEmail: user.email
        }
      }
    });
    const win = window.open(`${config.dashboardUrl}?action=signIn&email=${userEmail}`, '_blank');
    win?.focus();
  };

  const handleDisableUser = async () => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Are you sure?',
      content: 'The user will not be able to login anymore.',
      okText: 'Disable',
      okType: 'danger',
      onOk: () => disableUser({variables: {id: userId}})
    });
  };

  const handleDisableMFA = async () => {
    Modal.confirm({
      title: 'Are you sure?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Disable',
      okType: 'danger',
      content: (
        <>
          <p>
            You are going to disable SMS multi-factor authentication and remove the phone number
            from the user.
          </p>
          <p>
            User will be asked to set up a new phone number to enable SMS MFA again on the next
            login.
          </p>
        </>
      ),
      async onOk() {
        await updateUser({
          variables: {
            input: {
              id: userId,
              phoneNumber: null
            }
          }
        });
      }
    });
  };

  const handleEnableUser = async () => {
    return enableUser({variables: {id: userId}});
  };

  const title = (
    <>
      {user && (
        <Stack>
          <Avatar
            src={gravatar.url(user.email, {default: '404'})}
            style={{marginRight: 10, backgroundColor: colors.primary}}>
            {initials(user.email)}
          </Avatar>
          <span>{user.email}</span>
          <UserStatusTag user={user} style={{marginLeft: 15}} />
        </Stack>
      )}
    </>
  );

  const cardProps: CardProps = {
    style: {marginBottom: 20},
    loading,
    size: 'small'
  };

  const menuItems = [
    <Menu.Item key={1} onClick={impersonateUser}>
      Impersonate
    </Menu.Item>,
    <Menu.Item key={2} onClick={() => setIsResetPasswordOpen(true)}>
      Reset password
    </Menu.Item>
  ];

  if (user?.enabled) {
    menuItems.push(
      <Menu.Item key={4} onClick={handleDisableUser}>
        Disable user
      </Menu.Item>
    );
  } else {
    menuItems.push(
      <Menu.Item key={5} onClick={handleEnableUser}>
        Enable user
      </Menu.Item>
    );
  }

  if (user?.smsMfaEnabled) {
    menuItems.push(
      <Menu.Item key={3} onClick={handleDisableMFA}>
        Disable SMS MFA
      </Menu.Item>
    );
  }

  return (
    <>
      <PageHeader loading={loading} title={title} level={2}>
        <Dropdown overlay={<Menu>{menuItems}</Menu>} placement="bottomRight">
          <Button loading={isImpersonating || isEnabling || isDisabling}>
            Actions <DownOutlined />
          </Button>
        </Dropdown>
      </PageHeader>
      <Card
        title="User"
        extra={
          <Text type="secondary" copyable>
            {user?.id}
          </Text>
        }
        {...cardProps}>
        {user && (
          <PropertyList
            properties={[
              {
                label: 'Email',
                value: <EmailLink email={user.email} />
              },
              {
                label: 'Phone number',
                value: formatPhone(user.phoneNumber, null, true)
              },
              {
                label: 'Status',
                value: user.status
              },
              {
                label: 'Account ID',
                copyable: {text: user.accountId || ''},
                value: <Link to={`/accounts/${user.accountId}`}>{user.accountId}</Link>
              },
              {
                label: 'Account ID [TEST]',
                copyable: {text: user.testAccountId || ''},
                value: <Link to={`/accounts/${user.testAccountId}`}>{user.testAccountId}</Link>
              },
              {
                label: 'Referral code',
                value: user.referralCode
              },
              {label: 'Created', value: moment(user.createdAt).format('lll')},
              {
                label: 'Enabled',
                value: formatBoolean(user.enabled)
              },
              {
                label: 'Email verified',
                value: formatBoolean(user.emailVerified)
              },
              {
                label: 'Phone number verified',
                value: formatBoolean(user.phoneNumberVerified)
              },
              {
                label: 'SMS MFA Enabled',
                value: formatBoolean(user.smsMfaEnabled)
              }
            ]}
          />
        )}
      </Card>
      {user && (
        <ResetPassword
          user={user}
          isOpen={isResetPasswordOpen}
          onClose={() => setIsResetPasswordOpen(false)}
        />
      )}
    </>
  );
};
